import React, { useEffect, useState } from "react";
import {
  Heart,
  ShieldCheck,
  Users,
  TrendingUp,
  CheckCircle2,
  Calendar,
  Award,
  ArrowRight,
  Activity,
  Microscope,
  MapPin,
  Clock,
  GraduationCap,
  Zap,
  Minus,
  X,
  User,
  UserX,
} from "lucide-react";
import airhoster from "../assets/sponsorship/airhoster.png";
import SummaryApi from "../common"; // Ensure your SummaryApi path is correct
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
const SponsorshipPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
  });
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const getUserIdFromSession = () => {
    const sessionData = localStorage.getItem("user");
    if (!sessionData) return null;
    try {
      const user = JSON.parse(sessionData);
      // Checking both possible keys (user_id or _id)
      return user?.user_id || user?._id;
    } catch (err) {
      return null;
    }
  };

  // 2. Fetch User Details and map them to Form State
  const fetchAndFillUserDetails = async () => {
    const userId = getUserIdFromSession();
    if (!userId) return;

    setIsLoadingUser(true);
    try {
      const response = await fetch(SummaryApi.userDetailsById.url, {
        method: SummaryApi.userDetailsById.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: userId }),
      });

      const result = await response.json();

      // Map your API response keys to your formData state keys
      if (result.status && result.data) {
        setFormData({
          name: result.data.user_name || "",
          mobile: result.data.mobile_number || "",
        });
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setIsLoadingUser(false);
    }
  };

  // 3. Trigger whenever Modal opens OR user switches from Anonymous to Personal
  useEffect(() => {
    if (isModalOpen && !isAnonymous) {
      fetchAndFillUserDetails();
    }
  }, [isModalOpen, isAnonymous]);

 const stats = [
    "716 Million people in rural India lack adequate hygiene & healthcare access",
  "23 Million girls drop out of school due to menstrual stigma and pad inaccessibility",
  "1 in 5 girls never returns to school after her first period",
  "20–30% of women of reproductive age are affected by PCOD",
  "Up to 80% of PCOD cases remain undiagnosed in rural areas",
  "90% of rural women do not access preventive health screenings",
  ];

  const handleOpenModal = (tierData) => {
    setSelectedTier(tierData);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsAnonymous(false);
    setFormData({ name: "", mobile: "" });
  };

  const navigate = useNavigate();

  // ... inside SponsorshipPage component
  const handleCheckout = () => {
    // Logic for Anonymous
    if (isAnonymous) {
      navigate("/sakhi-checkout", {
        state: {
          selectedTier,
          userDetails: { type: "Anonymous" },
          isAnonymous: true,
        },
      });
      return;
    }

    // 2. Logic for Personal - Validation using react-hot-toast
    const { name, mobile } = formData;

    if (!name.trim()) {
      toast.error("Please enter your full name");
      return;
    }

    if (!mobile.trim()) {
      toast.error("Mobile number is required");
      return;
    }

    // Mobile Number Validation (exactly 10 digits)
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(mobile)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    // 3. Success: Navigate
    toast.success("Details confirmed!");
    navigate("/sakhi-checkout", {
      state: {
        selectedTier,
        userDetails: formData,
        isAnonymous: false,
      },
    });
  };
  return (
    <div className="bg-[#FFF0F3] min-h-screen font-sans text-slate-900 selection:bg-purple-200 overflow-x-hidden">
      {/* --- Checkout Modal --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-purple-950/40 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="p-8 border-b border-pink-50 flex justify-between items-center bg-pink-50/50">
              <div>
                <h3 className="text-2xl font-black text-purple-950 uppercase tracking-tighter">
                  Sponsor {selectedTier?.tier}
                </h3>
                <p className="text-slate-500 text-sm font-bold">
                  Complete your impact journey
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-pink-100 rounded-full transition-colors"
              >
                <X size={24} className="text-purple-900" />
              </button>
            </div>

            <div className="p-8">
              {/* Info Summary */}
              <div className="flex gap-4 mb-8">
                <div className="flex-1 bg-purple-50 p-4 rounded-2xl border border-purple-100">
                  <p className="text-[10px] font-black text-purple-400 uppercase">
                    Contribution
                  </p>
                  <p className="text-2xl font-black text-purple-900">
                    {selectedTier?.price}
                  </p>
                </div>
                <div className="flex-1 bg-pink-50 p-4 rounded-2xl border border-pink-100">
                  <p className="text-[10px] font-black text-pink-400 uppercase">
                    Impact
                  </p>
                  <p className="text-2xl font-black text-pink-600">
                    {selectedTier?.pads}
                  </p>
                </div>
              </div>

              {/* Anonymous Toggle */}
              <div className="flex bg-slate-100 p-1 rounded-xl mb-6">
                <button
                  onClick={() => setIsAnonymous(false)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold transition-all ${!isAnonymous ? "bg-white text-purple-800 shadow-sm" : "text-slate-500"}`}
                >
                  <User size={16} /> Personal
                </button>
                <button
                  onClick={() => setIsAnonymous(true)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold transition-all ${isAnonymous ? "bg-white text-purple-800 shadow-sm" : "text-slate-500"}`}
                >
                  <UserX size={16} /> Anonymous
                </button>
              </div>

              {!isAnonymous ? (
                <div className="space-y-4 relative">
                  {/* Optional: Loading Overlay */}
                  {isLoadingUser && (
                    <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-xl backdrop-blur-[1px]">
                      <div className="flex items-center gap-2 text-purple-800 font-bold">
                        <div className="w-4 h-4 border-2 border-purple-800 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-xs">Loading profile...</span>
                      </div>
                    </div>
                  )}

                  <input
                    type="text"
                    placeholder="Full Name"
                    className="w-full px-5 py-4 rounded-xl border border-slate-200 focus:ring-2 focus:ring-purple-500 outline-none transition-all font-medium"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                  <input
                    type="tel"
                    placeholder="Mobile Number"
                    className="w-full px-5 py-4 rounded-xl border border-slate-200 focus:ring-2 focus:ring-purple-500 outline-none transition-all font-medium"
                    value={formData.mobile}
                    maxLength={10} // Prevents more than 10 digits
                    onChange={(e) => {
                      // Only allows numbers to be typed
                      const val = e.target.value.replace(/\D/g, "");
                      setFormData({ ...formData, mobile: val });
                    }}
                  />
                </div>
              ) : (
                <div className="py-8 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                  <Heart
                    className="mx-auto text-pink-400 mb-2"
                    fill="currentColor"
                  />
                  <p className="text-slate-500 font-medium px-6">
                    Your kindness will be recorded as an anonymous contribution.
                  </p>
                </div>
              )}

              <button
                onClick={handleCheckout}
                className="w-full mt-8 bg-purple-800 text-white py-5 rounded-2xl font-black text-lg hover:bg-purple-900 transition-all shadow-xl shadow-purple-200 active:scale-95"
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Hero Section --- */}
      <section className="relative py-20 px-8 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-pink-200 text-purple-800 text-[11px] font-black mb-8 tracking-[0.2em] shadow-sm uppercase">
              SAKHI SPONSORSHIP
            </div>
            <h1 className="text-5xl lg:text-7xl font-black leading-[1.05] text-purple-950 mb-8">
              Be the reason her period is{" "}
              <span className="text-pink-600 italic">safe, dignified,</span> and
              worry-free.
            </h1>
            <p className="text-xl text-slate-600 mb-10 leading-relaxed font-medium">
              “I am supporting a woman — not funding a problem.” <br />
              <span className="text-base font-normal opacity-75">
                Eveya Sakhi ensures continuous access to period care, health
                support, and confidence — every month.
              </span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() =>
                  document
                    .getElementById("tiers")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="bg-purple-800 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-purple-900 transition shadow-2xl shadow-purple-200 flex items-center justify-center gap-3"
              >
                Sponsor Period Dignity <ArrowRight size={20} />
              </button>
            </div>
          </div>
          <div className="relative group">
            <div className="absolute inset-0 bg-white/50 rounded-[4rem] rotate-3 -z-10 border border-white"></div>
            <img
              src={airhoster}
              alt="Dignity"
              className="rounded-[4rem] shadow-2xl z-10 aspect-[4/5] object-cover border-8 border-white transition-transform group-hover:scale-[1.02] duration-500"
            />
          </div>
        </div>
      </section>

      {/* --- Marquee --- */}
      <div className="bg-purple-950 py-8 border-y-4 border-pink-500 relative overflow-hidden flex">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...stats, ...stats].map((stat, index) => (
            <div key={index} className="flex items-center px-10">
              <span className="text-white font-black uppercase tracking-widest text-sm opacity-90">
                {stat}
              </span>
              <Minus
                className="text-pink-500 mx-10 rotate-90"
                size={24}
                strokeWidth={4}
              />
            </div>
          ))}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 40s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `,
        }}
      />

      {/* --- Impact Levels Section --- */}
      <section id="tiers" className="py-24 px-8 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-black text-purple-950 mb-4 uppercase tracking-tighter">
            Choose Your <span className="text-pink-600">Impact Level</span>
          </h2>
          <p className="text-slate-500 font-bold text-lg">
            Sponsorship tiers for holistic support
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          <TierCard
            tier="Monthly"
            price="₹60"
            period="/month"
            pads="10 Pads"
            // benefits={["Products", "Monthly wellness tips", "Flexibility to pause"]}
            colorScheme="pink"
            onSelect={() =>
              handleOpenModal({
                tier: "Monthly",
                price: "₹60",
                pads: "10 Pads",
              })
            }
          />
          <TierCard
            tier="Quarterly"
            price="₹180"
            period="/quarter"
            pads="30 Pads"
            // benefits={["Products", "Quarterly updates", "Health consultation"]}
            colorScheme="pink"
            onSelect={() =>
              handleOpenModal({
                tier: "Quarterly",
                price: "₹180",
                pads: "30 Pads",
              })
            }
          />
          <TierCard
            tier="Half-Yearly"
            price="₹360"
            period="/6 months"
            pads="60 Pads"
            // benefits={["Products", "Education access", "Health consultations", "Peer support"]}
            colorScheme="teal"
            onSelect={() =>
              handleOpenModal({
                tier: "Half-Yearly",
                price: "₹360",
                pads: "60 Pads",
              })
            }
          />
          <TierCard
            tier="Annual"
            price="₹720"
            period="/year"
            subPrice="Full Package"
            pads="120 Pads"
            // benefits={["Products", "Monthly updates", "Health access", "Community support", "Annual report"]}
            colorScheme="teal"
            featured={true}
            onSelect={() =>
              handleOpenModal({
                tier: "Annual",
                price: "₹720",
                pads: "120 Pads",
              })
            }
          />
        </div>
      </section>

      {/* ... (Rest of the sections: Pillars, Outcomes, Matrix, Footer) ... */}
      {/* (Keep the rest of your existing JSX sections here) */}

      {/* --- The 4 Pillars --- */}
      {/* <section id="pillars" className="py-24 px-8 max-w-7xl mx-auto">
        <div className="mb-20 text-center">
          <h2 className="text-4xl font-black text-purple-950 mb-4 uppercase tracking-tighter">
            The Eveya Sakhi Model
          </h2>
          <p className="text-slate-500 font-bold italic">
            “Sakhi is built as a scalable sponsorship model — not one-time aid.”
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <PillarCard
            icon={<ShieldCheck className="text-pink-500" />}
            title="Pillar 1: Care"
            desc="120 Premium pads/year. Dioxin-free, breathable, and hormone-safe."
          />
          <PillarCard
            icon={<Microscope className="text-pink-600" />}
            title="Pillar 2: Screening"
            desc="PCOD, Diabetes, and Cardiac profiles via Thyrocare partnership."
          />
          <PillarCard
            icon={<MapPin className="text-pink-500" />}
            title="Pillar 3: Local Employment"
            desc="Female health coordinators hired from the village for monthly tracking."
          />
          <PillarCard
            icon={<Users className="text-pink-600" />}
            title="Pillar 4: Education"
            desc="Quarterly community awareness, myth-busting, and nutritional counseling."
          />
        </div>
      </section> */}
   <section id="tiers" className="py-24 px-8 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-black text-purple-950 mb-4 uppercase tracking-tighter">
            Sponsorship Plans
          </h2>
          <p className="text-slate-500 font-medium italic">
            Framed as "Continuity of Care" to build lasting confidence.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <TierCard1
            tier="Monthly Sakhi"
            // target="First-time"
            message="Ensure one woman never worries about her next cycle."
          />
          <TierCard1
            tier="Quarterly Sakhi"
            // target="Families"
            message="Consistency that builds lasting confidence."
          />
          <TierCard1
            tier="Half-Yearly Sakhi"
            // target="Teams"
            message="Stability that changes habits, not just outcomes."
          />
          <TierCard1
            tier="Annual Sakhi"
            // target="Investors"
            message="One year. One woman. Complete dignity."
            featured={true}
          />
        </div>
      </section>
      {/* --- Footer CTA --- */}
      <footer className="bg-white py-24 px-8 text-center rounded-t-[5rem] shadow-inner">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-black text-purple-950 mb-8 italic">
            Stand with her.
          </h2>
          <p className="text-xl text-slate-500 mb-12 font-medium italic">
            "Eveya Sakhi is not about giving pads. It's about ensuring no woman
            has to plan her life around uncertainty."
          </p>
          <button
            onClick={() =>
              document
                .getElementById("tiers")
                .scrollIntoView({ behavior: "smooth" })
            }
            className="bg-purple-800 text-white px-12 py-6 rounded-3xl font-black text-2xl hover:bg-purple-900 transition shadow-2xl shadow-purple-200 active:scale-95"
          >
            Become an Eveya Sakhi
          </button>
        </div>
      </footer>
    </div>
  );
};

/* --- Updated Helper Components --- */

const TierCard = ({
  tier,
  price,
  period,
  subPrice,
  pads,
  benefits,
  colorScheme,
  onSelect,
}) => {
  const isPink = colorScheme === "pink";

  return (
    <div
      className={`flex flex-col p-8 rounded-[2rem] transition-all duration-300 border ${
        isPink ? "bg-[#ffebee] border-pink-100" : "bg-[#e0f2f1] border-teal-100"
      } hover:shadow-2xl hover:-translate-y-1`}
    >
      <div className="text-center mb-6">
        <h3
          className={`text-2xl font-bold mb-4 ${isPink ? "text-pink-600" : "text-teal-700"}`}
        >
          {tier}
        </h3>
        <div className="flex items-baseline justify-center">
          <span className="text-3xl font-black text-slate-800">{price}</span>
          <span className="text-slate-500 font-bold ml-1">{period}</span>
        </div>
        <p className="text-slate-400 text-sm mt-1 font-medium italic">
          {subPrice}
        </p>
      </div>

      <div className="h-px bg-slate-200 w-full mb-6"></div>

      <div className="flex-grow">
        <p className="text-center font-black text-slate-800 uppercase tracking-widest text-xs mb-6">
          Benefits
        </p>
        <div className="flex items-center gap-3 mb-4">
          <CheckCircle2
            size={16}
            className={isPink ? "text-pink-500" : "text-teal-600"}
          />
          <span className="text-sm font-bold text-slate-700">
            {pads} included
          </span>
        </div>
        {/* <ul className="space-y-4">
          {benefits.map((benefit, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle2 size={16} className={isPink ? "text-pink-400" : "text-teal-500"} />
              <span className="text-sm text-slate-600 font-medium">{benefit}</span>
            </li>
          ))}
        </ul> */}
      </div>

      <button
        onClick={onSelect}
        className={`w-full py-4 mt-8 rounded-xl font-black text-sm text-white uppercase tracking-widest transition-all ${
          isPink
            ? "bg-red-500 hover:bg-red-600 shadow-red-100"
            : "bg-[#008080] hover:bg-[#006666] shadow-teal-100"
        } shadow-lg active:scale-95`}
      >
        Choose {tier}
      </button>
    </div>
  );
};
const TierCard1 = ({ tier, target, message, featured = false }) => (
  <div
    className={`relative p-8 rounded-[3rem] transition-all duration-500 ${
      featured
        ? "bg-purple-800 text-white shadow-2xl scale-105 z-10"
        : "bg-white border border-pink-100 hover:shadow-lg"
    }`}
  >
    {featured && (
      <div className="absolute -top-4 right-10 bg-pink-500 text-white px-3 py-1 rounded-full text-[10px] font-black tracking-widest">
        MOST IMPACTFUL
      </div>
    )}
    {/* <p
      className={`text-[10px] font-black uppercase tracking-widest mb-1 ${featured ? "text-pink-300" : "text-slate-400"}`}
    >
      FOR: {target}
    </p> */}
    <h3 className="text-2xl font-black mb-4 uppercase tracking-tighter">
      {tier}
    </h3>
    <p
      className={`text-sm mb-10  leading-relaxed italic font-medium ${featured ? "text-white/80" : "text-slate-500"}`}
    >
      "{message}"
    </p>
    {/* <button
      className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${
        featured
          ? "bg-pink-500 text-white hover:bg-pink-600"
          : "bg-pink-50 text-purple-900 hover:bg-pink-100"
      }`}
    >
      Sponsor
    </button> */}
  </div>
);
// ... Rest of PillarCard, ImpactItem, MatrixRow, etc. stay as they were
const PillarCard = ({ icon, title, desc }) => (
  <div className="bg-white p-8 rounded-[2.5rem] border border-pink-100 shadow-sm hover:shadow-xl transition-all group">
    <div className="w-14 h-14 bg-pink-50 rounded-2xl flex items-center justify-center text-purple-700 mb-6 group-hover:bg-purple-700 group-hover:text-white transition-colors">
      {React.cloneElement(icon, { size: 28 })}
    </div>
    <h4 className="text-lg font-black text-purple-950 mb-2 uppercase tracking-tighter">
      {title}
    </h4>
    <p className="text-xs text-slate-500 font-medium leading-relaxed">{desc}</p>
  </div>
);

export default SponsorshipPage;
